import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import i18n from 'i18next';
import { getKgOrTon, getTon, useBasePath } from './utils/utilities';
import AdviceInformation from './AdviceInformation';
import usePageView from './hooks/usePageView';

interface Product {
    id: number;
    name: string;
    product_group: string;
    boiler_type: string;
    unit_type: string;
    disclaimer: string;
    description: string;
    positioning: string;
    brochure_url: string;
    image_url: string;
    heat365_url: string;
}

interface Solution {
    energy_costs: string;
    energy_savings: string;
    co2_savings: string;
    price: string;
    product: Product;
}

type Recommendations = {
    solutions: { [key: string]: Solution[] };
};

export default function Results() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const basePath = useBasePath();
    const uuid = localStorage.getItem('uuid');
    const { data, isLoading } = useSWR<Recommendations>(
        `/submission/${uuid}/recommendations`
    );

    usePageView();

    if (!data || isLoading) return <div>Loading...</div>;

    return (
        <div className="advices-suisse">
            <h2 className="beta-heading advices-suisse__main-title">
                {t('Our recommendation')}
            </h2>

            <div className="advices-suisse__links">
                <button
                    className="advices-suisse__back-button"
                    type={'button'}
                    onClick={() => navigate(`${basePath}/`)}
                >
                    {t('< Back')}
                </button>
                <button
                    className="advices-suisse__back-button"
                    type={'button'}
                    onClick={() => navigate(`${basePath}/refinement/`)}
                >
                    {t('Refinement')}
                </button>
            </div>

            <div className="advice-suisse-container">
                {Object.entries(data.solutions).map(([title, solutions]) => {
                    return (
                        <div className="advice-suisse-wrapper" key={title}>
                            <h2 className="beta-heading advice-suisse__title">
                                {title}
                            </h2>

                            <ul className="advice-suisse-group">
                                {solutions.map((solution) => (
                                    <li
                                        className="advice-suisse"
                                        key={solution.product.id}
                                    >
                                        {solution.product.id ? (
                                            <>
                                                <div className="advice-suisse__overview-container">
                                                    <span className="advice-suisse__subtitle">
                                                        {t('Option ')}
                                                    </span>
                                                    <div className="advice-suisse__overview">
                                                        <div className="advice-suisse__item">
                                                            <img
                                                                className="advice-suisse__img"
                                                                alt=""
                                                                src={
                                                                    solution
                                                                        .product
                                                                        .image_url
                                                                }
                                                            />

                                                            <div className="advice-suisse__content">
                                                                <h3 className="gamma-heading advice-suisse__content-title">
                                                                    {
                                                                        solution
                                                                            .product
                                                                            .name
                                                                    }
                                                                </h3>

                                                                <ul className="kpi-list">
                                                                    <li className="kpi-list__item">
                                                                        <div className="kpi-list__item-container">
                                                                            <span className="kpi__icon iconlist__house-invest-chf" />
                                                                            <div>
                                                                                <span className="kpi-list__key">
                                                                                    {t(
                                                                                        'Investment costs: '
                                                                                    )}
                                                                                </span>
                                                                                <span className="kpi-list__value">
                                                                                    {t(
                                                                                        'from'
                                                                                    )}{' '}
                                                                                    CHF{' '}
                                                                                    {solution.price &&
                                                                                        t(
                                                                                            'intlNumber',
                                                                                            {
                                                                                                val: solution.price,
                                                                                            }
                                                                                        )}
                                                                                </span>
                                                                            </div>
                                                                        </div>

                                                                        {solution
                                                                            .product
                                                                            .heat365_url &&
                                                                            i18n.language ===
                                                                                'de' && (
                                                                                <span className="kpi-list__rent">
                                                                                    {t(
                                                                                        'You can also rent this product instead of buying – for a monthly fee. With Heat365, our partner company.'
                                                                                    )}
                                                                                    <span className="kpi-list__rent-link">
                                                                                        <a
                                                                                            href={
                                                                                                solution
                                                                                                    .product
                                                                                                    .heat365_url
                                                                                            }
                                                                                            target="_blank"
                                                                                            rel={
                                                                                                'noreferrer'
                                                                                            }
                                                                                        >
                                                                                            {t(
                                                                                                'Check it out'
                                                                                            )}
                                                                                        </a>

                                                                                        .
                                                                                    </span>
                                                                                    <span className="kpi-list__rent-heat">
                                                                                        <svg className="kpi-list__rent-icon">
                                                                                            <use xlinkHref="#heat365" />
                                                                                        </svg>
                                                                                    </span>
                                                                                </span>
                                                                            )}
                                                                    </li>
                                                                    <li className="kpi-list__item">
                                                                        <div className="kpi-list__item-container">
                                                                            <span className="kpi__icon iconlist__costs" />
                                                                            <div>
                                                                                <span className="kpi-list__key">
                                                                                    {t(
                                                                                        'Energy costs per year:'
                                                                                    )}
                                                                                </span>
                                                                                <span className="kpi-list__value">
                                                                                    CHF{' '}
                                                                                    {solution.energy_costs &&
                                                                                        t(
                                                                                            'intlNumber',
                                                                                            {
                                                                                                val: solution.energy_costs,
                                                                                            }
                                                                                        )}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                    <li className="kpi-list__item">
                                                                        <div className="kpi-list__item-container">
                                                                            <span className="kpi__icon iconlist__savings" />
                                                                            <div>
                                                                                <span className="kpi-list__key">
                                                                                    {t(
                                                                                        'Energy savings per year:'
                                                                                    )}
                                                                                </span>
                                                                                <span className="kpi-list__value">
                                                                                    CHF{' '}
                                                                                    {solution.energy_savings &&
                                                                                        t(
                                                                                            'intlNumber',
                                                                                            {
                                                                                                val: solution.energy_savings,
                                                                                            }
                                                                                        )}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                    <li className="kpi-list__item">
                                                                        <div className="kpi-list__item-container">
                                                                            <span className="kpi__icon iconlist__leaf" />
                                                                            <div>
                                                                                <span className="kpi-list__key">
                                                                                    {t(
                                                                                        'CO2 savings per year:'
                                                                                    )}
                                                                                </span>
                                                                                <span className="kpi-list__value">
                                                                                    {getTon(
                                                                                        parseInt(
                                                                                            solution.co2_savings,
                                                                                            10
                                                                                        )
                                                                                    )}
                                                                                    {getKgOrTon(
                                                                                        parseInt(
                                                                                            solution.co2_savings,
                                                                                            10
                                                                                        )
                                                                                    )}

                                                                                    *
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                    <li className="kpi-list__item">
                                                                        <span className="kpi-list__hint">
                                                                            {t(
                                                                                '* 2.0 t Co2 = 1 roundtrip by flight Zurich to New York (12600km)'
                                                                            )}
                                                                        </span>
                                                                    </li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                        {(solution.product
                                                            .brochure_url ||
                                                            solution.product
                                                                .description ||
                                                            solution.product
                                                                .disclaimer) && (
                                                            <AdviceInformation
                                                                description={
                                                                    solution
                                                                        .product
                                                                        .description
                                                                }
                                                                disclaimer={
                                                                    solution
                                                                        .product
                                                                        .disclaimer
                                                                }
                                                                brochure_url={
                                                                    solution
                                                                        .product
                                                                        .brochure_url
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                                <a
                                                    className="advice-information__quotation button button--quotation"
                                                    href={`${basePath}/offer/${solution.product.id}`}
                                                >
                                                    {t(
                                                        'Request free quotation'
                                                    )}
                                                </a>
                                            </>
                                        ) : (
                                            <>
                                                <span className="advice-suisse__subtitle">
                                                    {t('Option')}
                                                </span>
                                                <h2 className="beta-heading advice-suisse__title">
                                                    {title}
                                                </h2>
                                                <p>
                                                    {t(
                                                        'There is no boiler which meets the given requirements'
                                                    )}
                                                </p>
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </div>

            <hr />

            <p className="subtext disclaimer">
                {t(
                    'Vaillant is not liable for any damages that may occur based on the results of the heatpump selector.'
                )}
            </p>
        </div>
    );
}
